import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {DeploymentEnvironment, IBusinessPartner} from '@service-and-repairs/awpintegrationlib';
import {environment} from '@environments/environment';
import {filter, map, Subscription} from 'rxjs';
import {ThinClientModeService} from '../../../services/thin-client-mode.service';
import {ConfigurationLoader} from '../../configuration/services/configuration.loader';
import {EtkConfiguration} from '../../configuration/interfaces/etk-configuration';
import {OutletConfiguration} from '../../configuration/interfaces/outlet-configuration';
import {OutletRelatedConfiguration} from '../../configuration/interfaces/outlet-related-configuration';
import {ServiceCase} from '../../service-case/models/service-case';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {AnalyticsAction} from '../enums/analytics-action';
import {AnalyticsEventType} from '../enums/analytics.event-type';
import {CONFIG_CHANGE_RELOAD_MARKER, ProcessKpiOptInOrigin} from '../enums/process-kpi-opt-in-origin';
import {AnalyticsEvent} from '../interfaces/analytics.event';
import {AnalyticsEventPayload} from '../interfaces/analytics.event.payload';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly userService: UserService = inject(UserService);
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);

  private user: UserData;
  private processKpiOptInSubscription: Subscription;

  constructor() {
    this.userService.user$.subscribe((user: UserData) => this.user = user);
    this.userService.loggedOut.subscribe((): void => this.postUserLoggedOutEvent());
  }

  postUserLoggedInEvent(): void {
    const origin: string = ThinClientModeService.thinClientMode ? 'thinclient' : 'webbrowser';
    const payload: AnalyticsEventPayload = this.createPayload(AnalyticsAction.LOGIN, origin);
    payload.login = this.user.getSettingsId();
    const event: AnalyticsEvent = this.createEvent(AnalyticsEventType.USER, payload);
    this.postEvent(event);
    this.subscribeToProcessKpiOptIn();
  }

  /**
   * Sends the process KPI opt-in flag every time an outlet configuration was loaded (login, outlet or vehicle type
   * switch, reload after saving the configuration). Re-sending unchanged values is intended: it recovers a failed
   * transmission of the latest change. Emissions without outlet configuration (load error) are ignored.
   */
  private subscribeToProcessKpiOptIn(): void {
    if (this.processKpiOptInSubscription) {
      return;
    }
    let origin: ProcessKpiOptInOrigin = this.consumeConfigChangeReloadMarker()
      ? ProcessKpiOptInOrigin.CONFIG_CHANGE
      : ProcessKpiOptInOrigin.LOGIN;
    this.processKpiOptInSubscription = this.configurationLoader.configChanged
      .pipe(
        map((config: { outletRelatedConfig: OutletRelatedConfiguration, etkConfig: EtkConfiguration }):
          OutletConfiguration => config?.outletRelatedConfig?.outletConfiguration),
        filter((outletConfiguration: OutletConfiguration): boolean => outletConfiguration != undefined)
      )
      .subscribe((outletConfiguration: OutletConfiguration): void => {
        this.postProcessKpiOptInEvent(origin, outletConfiguration.reportingProcessKpiIsEnabled ?? false);
        origin = ProcessKpiOptInOrigin.OUTLET_CHANGE;
      });
  }

  private consumeConfigChangeReloadMarker(): boolean {
    const markerPresent: boolean = sessionStorage.getItem(CONFIG_CHANGE_RELOAD_MARKER) === 'true';
    sessionStorage.removeItem(CONFIG_CHANGE_RELOAD_MARKER);
    return markerPresent;
  }

  private postProcessKpiOptInEvent(origin: ProcessKpiOptInOrigin, processKpiOptIn: boolean): void {
    const payload: AnalyticsEventPayload = {
      action: AnalyticsAction.PROCESS_KPI_OPT_IN,
      service: 'webapp',
      origin,
      processKpiOptIn
    };
    const event: AnalyticsEvent = this.createEvent(AnalyticsEventType.OUTLET, payload);
    this.postEvent(event);
  }

  postUserLoggedOutEvent(): void {
    const origin: string = ThinClientModeService.thinClientMode ? 'thinclient' : 'webbrowser';
    const payload: AnalyticsEventPayload = this.createPayload(AnalyticsAction.LOGOUT, origin);
    payload.login = this.user.getSettingsId();
    const event: AnalyticsEvent = this.createEvent(AnalyticsEventType.USER, payload);
    this.postEvent(event);
  }

  postUserSettingsChangedEvent(key: string, value: string): void {
    const payload: AnalyticsEventPayload = this.createPayload(AnalyticsAction.SETTINGS_CHANGED, key + '#' + value);
    const event: AnalyticsEvent = this.createEvent(AnalyticsEventType.USER, payload);
    this.postEvent(event);
  }

  postKeyPoolButtonClickedEvent(): void {
    const payload = this.createPayload(AnalyticsAction.CLICKED, 'key-pool');
    const event = this.createEvent(AnalyticsEventType.NAVIGATION, payload);
    this.postEvent(event);
  }

  postVehicleSearchEvent(action: AnalyticsAction, origin: string): void {
    const payload = this.createPayload(action, origin);
    const event = this.createEvent(AnalyticsEventType.VEHICLE, payload);
    this.postEvent(event);
  }

  postServiceCaseTabEvent(action: AnalyticsAction,
                           origin: string,
                           serviceCase: ServiceCase): void {
    const payload: AnalyticsEventPayload = this.createPayload(action, origin, serviceCase);
    const event: AnalyticsEvent = this.createEvent(AnalyticsEventType.SERVICE_CASE, payload);
    this.postEvent(event);
  }

  postDmsRequestEvent(origin: string): void {
    const payload = this.createPayload(AnalyticsAction.CLICKED, origin);
    const event = this.createEvent(AnalyticsEventType.DMS, payload);
    this.postEvent(event);
  }

  postNavigationEvent(destination: string, origin: string): void {
    const payload = this.createPayload(AnalyticsAction.NAVIGATION, origin);
    payload.destination = destination;
    const event = this.createEvent(AnalyticsEventType.NAVIGATION, payload);
    this.postEvent(event);
  }

  private createPayload(action: AnalyticsAction, origin: string, serviceCase?: ServiceCase): AnalyticsEventPayload {
    return {
      action,
      service: 'webapp',
      origin,
      serviceCaseId: serviceCase?.getExternalId()
    };
  }

  /**
   * Returns undefined if the user has no business partner, because the event cannot be assigned to an outlet.
   */
  private createEvent(eventType: AnalyticsEventType, eventPayload: AnalyticsEventPayload): AnalyticsEvent | undefined {
    const bp: IBusinessPartner = this.user.getBusinessPartner();
    if (!bp) {
      console.warn(`[Webapp] [Analytics] Skipped ${eventType} event ${eventPayload.action}: no business partner set.`);
      return undefined;
    }
    return {
      source: 'AWP',
      environment: environment.deploymentEnvironment,
      created: new Date().getTime(),
      session: sessionStorage.getItem('cavors-session-id'),
      language: this.user.getLocale().replace('-', '_'),
      country: this.user.getB2XCountryCode(),
      payload: eventPayload,
      'auth-context': {
        context: this.user.getContext(),
        'client-id': bp.getDistributionPartnerNumber() + '-' + bp.getOutletNumber()
      },
      'event-type': eventType
    };
  }

  private postEvent(event: AnalyticsEvent | undefined): void {
    if (!event?.environment || event.environment === DeploymentEnvironment.DEV_LOCAL) {
      return;
    }
    const headers = new HttpHeaders().append('Content-Type', 'application/vnd.air-analytics.v1+json');
    this.httpClient.post('/api/canal/event/' + event.session, event, {headers}).subscribe();
  }
}
